import React from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text, LoadingSpinner } from '../ui'
import { appointmentFormInputClass } from './appointmentFormUi'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { isSlotBookable } from '../../services/supportCenterParameters'
import type { SupportCenterAttendantOption } from '../../types/supportCenterAppointments'
import type { SupportCenterSchedulingLimits } from '../../types/supportCenterParameters'

export type AgendaAvailabilitySelection = {
  professionalUserId: string
  scheduledAtLocal: string
}

type AgendaAvailabilityModalProps = {
  open: boolean
  onClose: () => void
  professionals: SupportCenterAttendantOption[]
  /** Quando true, só consulta/permite o profissional já selecionado (ou o único). */
  lockProfessionalToSelf: boolean
  initialProfessionalId: string
  minYmd: string
  maxYmd: string
  schedulingLimits: SupportCenterSchedulingLimits
  onSelect: (selection: AgendaAvailabilitySelection) => void
}

type SlotEntry = {
  professionalId: string
  professionalName: string
  horario: string
  scheduledAt: string
}

type DaySlots = Map<string, SlotEntry[]>

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']
const MONTH_LABELS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
]

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function ymdFromParts(y: number, m: number, d: number): string {
  return `${y}-${pad2(m + 1)}-${pad2(d)}`
}

function parseYmd(ymd: string): { y: number; m: number; d: number } | null {
  const match = ymd.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) }
}

function formatBrDate(ymd: string): string {
  const parts = parseYmd(ymd)
  if (!parts) return ymd
  return `${pad2(parts.d)}/${pad2(parts.m + 1)}/${parts.y}`
}

function monthKey(y: number, m: number): string {
  return `${y}-${pad2(m + 1)}`
}

function compareYmd(a: string, b: string): number {
  return a.slice(0, 10).localeCompare(b.slice(0, 10))
}

function buildMonthCells(year: number, month: number): Array<{ ymd: string | null; day: number | null }> {
  const first = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startWeekday = first.getDay()
  const cells: Array<{ ymd: string | null; day: number | null }> = []
  for (let i = 0; i < startWeekday; i++) {
    cells.push({ ymd: null, day: null })
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ ymd: ymdFromParts(year, month, day), day })
  }
  while (cells.length % 7 !== 0) {
    cells.push({ ymd: null, day: null })
  }
  return cells
}

function scheduledAtToLocal(scheduledAt: string): string {
  const raw = scheduledAt.includes('T') ? scheduledAt : scheduledAt.replace(' ', 'T')
  return raw.slice(0, 16)
}

/**
 * Modal de disponibilidade: calendário + próxima data livre para a especialidade/profissional.
 * Fonte: mesma API `/centralamparo/disponibilidade` usada no formulário.
 */
export const AgendaAvailabilityModal: React.FC<AgendaAvailabilityModalProps> = ({
  open,
  onClose,
  professionals,
  lockProfessionalToSelf,
  initialProfessionalId,
  minYmd,
  maxYmd,
  schedulingLimits,
  onSelect,
}) => {
  const titleId = 'agenda-availability-modal-title'
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  /** Todos os slots carregados (todos os profissionais consultados). */
  const [allSlots, setAllSlots] = React.useState<SlotEntry[]>([])
  const [filterProfessionalId, setFilterProfessionalId] = React.useState<string>('')
  const [selectedYmd, setSelectedYmd] = React.useState<string>('')
  const [viewYear, setViewYear] = React.useState(() => {
    const p = parseYmd(minYmd)
    return p?.y ?? new Date().getFullYear()
  })
  const [viewMonth, setViewMonth] = React.useState(() => {
    const p = parseYmd(minYmd)
    return p?.m ?? new Date().getMonth()
  })

  const professionalsToQuery = React.useMemo(() => {
    if (lockProfessionalToSelf) {
      if (initialProfessionalId) {
        return professionals.filter((p) => p.id === initialProfessionalId)
      }
      return professionals
    }
    return professionals
  }, [professionals, lockProfessionalToSelf, initialProfessionalId])

  React.useEffect(() => {
    if (!open) return
    const locked =
      lockProfessionalToSelf &&
      (initialProfessionalId || (professionals.length === 1 ? professionals[0].id : ''))
    setFilterProfessionalId(locked || initialProfessionalId || '')
    const start = parseYmd(minYmd)
    if (start) {
      setViewYear(start.y)
      setViewMonth(start.m)
    }
    setSelectedYmd('')
    setError(null)
  }, [open, lockProfessionalToSelf, initialProfessionalId, professionals, minYmd])

  React.useEffect(() => {
    if (!open || professionalsToQuery.length === 0) {
      setAllSlots([])
      return
    }
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const now = new Date()
        const results = await Promise.all(
          professionalsToQuery.map(async (pro) => {
            try {
              const slots = await supportCenterAppointmentsService.listAvailableSlots(
                { userId: pro.id, email: pro.email },
                minYmd,
                maxYmd
              )
              return slots
                .filter((s) => isSlotBookable(s.scheduled_at, schedulingLimits, now))
                .map(
                  (s): SlotEntry => ({
                    professionalId: pro.id,
                    professionalName: pro.name,
                    horario: s.horario.slice(0, 5),
                    scheduledAt: s.scheduled_at,
                  })
                )
            } catch {
              return [] as SlotEntry[]
            }
          })
        )
        if (cancelled) return
        const flat = results.flat().sort((a, b) => {
          const byDate = a.scheduledAt.localeCompare(b.scheduledAt)
          if (byDate !== 0) return byDate
          return a.professionalName.localeCompare(b.professionalName, 'pt-BR')
        })
        setAllSlots(flat)
        if (flat.length === 0) {
          setError(`Nenhum horário livre até ${formatBrDate(maxYmd)}.`)
        }
      } catch (e) {
        if (!cancelled) {
          setAllSlots([])
          setError(e instanceof Error ? e.message : 'Não foi possível carregar a disponibilidade.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, professionalsToQuery, minYmd, maxYmd, schedulingLimits])

  const filteredSlots = React.useMemo(() => {
    if (!filterProfessionalId) return allSlots
    return allSlots.filter((s) => s.professionalId === filterProfessionalId)
  }, [allSlots, filterProfessionalId])

  const slotsByDay = React.useMemo(() => {
    const map: DaySlots = new Map()
    for (const slot of filteredSlots) {
      const ymd = slot.scheduledAt.slice(0, 10)
      const list = map.get(ymd) ?? []
      list.push(slot)
      map.set(ymd, list)
    }
    return map
  }, [filteredSlots])

  const nextFree = React.useMemo(() => {
    return filteredSlots[0] ?? null
  }, [filteredSlots])

  React.useEffect(() => {
    if (!open || loading) return
    if (selectedYmd && slotsByDay.has(selectedYmd)) return
    if (nextFree) {
      const ymd = nextFree.scheduledAt.slice(0, 10)
      setSelectedYmd(ymd)
      const parts = parseYmd(ymd)
      if (parts) {
        setViewYear(parts.y)
        setViewMonth(parts.m)
      }
    } else {
      setSelectedYmd('')
    }
  }, [open, loading, nextFree, selectedYmd, slotsByDay])

  const minMonth = React.useMemo(() => parseYmd(minYmd), [minYmd])
  const maxMonth = React.useMemo(() => parseYmd(maxYmd), [maxYmd])

  const canPrevMonth = React.useMemo(() => {
    if (!minMonth) return false
    return monthKey(viewYear, viewMonth) > monthKey(minMonth.y, minMonth.m)
  }, [viewYear, viewMonth, minMonth])

  const canNextMonth = React.useMemo(() => {
    if (!maxMonth) return false
    return monthKey(viewYear, viewMonth) < monthKey(maxMonth.y, maxMonth.m)
  }, [viewYear, viewMonth, maxMonth])

  const monthCells = React.useMemo(
    () => buildMonthCells(viewYear, viewMonth),
    [viewYear, viewMonth]
  )

  const daySlots = selectedYmd ? slotsByDay.get(selectedYmd) ?? [] : []

  /** Agrupa por horário quando há vários profissionais no mesmo slot. */
  const hoursGrouped = React.useMemo(() => {
    const map = new Map<string, SlotEntry[]>()
    for (const slot of daySlots) {
      const list = map.get(slot.horario) ?? []
      list.push(slot)
      map.set(slot.horario, list)
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [daySlots])

  const showProfessionalFilter = !lockProfessionalToSelf && professionals.length > 1

  const handlePickSlot = (slot: SlotEntry) => {
    onSelect({
      professionalUserId: slot.professionalId,
      scheduledAtLocal: scheduledAtToLocal(
        `${slot.scheduledAt.slice(0, 10)}T${slot.horario}:00`
      ),
    })
    onClose()
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[min(90vh,720px)] flex flex-col overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading as="h3" size="2xl" id={titleId} className="text-gray-900 dark:text-white">
              Disponibilidade da agenda
            </Heading>
            <Text size="sm" color="muted" className="mt-0.5">
              Próximas datas livres considerando especialidade e profissional.
            </Text>
          </div>
          <Button variant="outline" onClick={onClose} className="shrink-0">
            Fechar
          </Button>
        </div>

        <div className="px-5 py-4 flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <LoadingSpinner />
              <Text size="sm" color="muted">
                Consultando disponibilidade...
              </Text>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-stretch gap-3 shrink-0">
                {showProfessionalFilter ? (
                  <div className="min-w-[220px] flex-1">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Profissional
                    </label>
                    <select
                      className={appointmentFormInputClass}
                      value={filterProfessionalId}
                      onChange={(e) => setFilterProfessionalId(e.target.value)}
                    >
                      <option value="">Qualquer profissional da especialidade</option>
                      {professionals.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {nextFree ? (
                  <div className="flex flex-1 min-w-[260px] items-center justify-between gap-3 rounded-lg border border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700 px-3 py-2">
                    <div className="min-w-0">
                      <Text
                        size="xs"
                        className="font-medium text-primary-800 dark:text-primary-200 uppercase tracking-wide"
                      >
                        Próxima data disponível
                      </Text>
                      <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-semibold text-primary-900 dark:text-primary-100">
                          {formatBrDate(nextFree.scheduledAt.slice(0, 10))} às {nextFree.horario}
                        </span>
                        <span className="text-xs text-primary-800/80 dark:text-primary-200/80 truncate">
                          · {nextFree.professionalName}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700"
                      onClick={() => handlePickSlot(nextFree)}
                    >
                      Usar
                    </button>
                  </div>
                ) : error ? (
                  <div className="flex-1 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                    {error}
                  </div>
                ) : allSlots.length > 0 && filteredSlots.length === 0 ? (
                  <div className="flex-1 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
                    Nenhum horário livre para este profissional no período. Tente outro filtro.
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-4 items-stretch flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
                <div className="shrink-0 md:self-start">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      disabled={!canPrevMonth}
                      onClick={() => {
                        if (viewMonth === 0) {
                          setViewYear((y) => y - 1)
                          setViewMonth(11)
                        } else {
                          setViewMonth((m) => m - 1)
                        }
                      }}
                      className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30 dark:text-gray-300 dark:hover:bg-gray-700"
                      aria-label="Mês anterior"
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                      {MONTH_LABELS[viewMonth]} {viewYear}
                    </Text>
                    <button
                      type="button"
                      disabled={!canNextMonth}
                      onClick={() => {
                        if (viewMonth === 11) {
                          setViewYear((y) => y + 1)
                          setViewMonth(0)
                        } else {
                          setViewMonth((m) => m + 1)
                        }
                      }}
                      className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-30 dark:text-gray-300 dark:hover:bg-gray-700"
                      aria-label="Próximo mês"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-1 text-center mb-1">
                    {WEEKDAY_LABELS.map((label) => (
                      <div
                        key={label}
                        className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 py-1"
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {monthCells.map((cell, idx) => {
                      if (!cell.ymd || cell.day == null) {
                        return <div key={`empty-${idx}`} className="h-8" />
                      }
                      const inRange =
                        compareYmd(cell.ymd, minYmd) >= 0 && compareYmd(cell.ymd, maxYmd) <= 0
                      const hasSlots = inRange && slotsByDay.has(cell.ymd)
                      const isSelected = cell.ymd === selectedYmd
                      const isNext =
                        nextFree != null && cell.ymd === nextFree.scheduledAt.slice(0, 10)

                      return (
                        <button
                          key={cell.ymd}
                          type="button"
                          disabled={!hasSlots}
                          onClick={() => setSelectedYmd(cell.ymd!)}
                          className={[
                            'relative h-8 rounded-lg text-sm transition-colors',
                            !inRange || !hasSlots
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : isSelected
                                ? 'bg-primary-600 text-white font-semibold'
                                : isNext
                                  ? 'bg-primary-100 text-primary-900 font-semibold ring-2 ring-primary-500 dark:bg-primary-900/40 dark:text-primary-100'
                                  : 'bg-primary-50 text-primary-800 hover:bg-primary-100 dark:bg-primary-900/20 dark:text-primary-100 dark:hover:bg-primary-900/40',
                          ].join(' ')}
                        >
                          {cell.day}
                          {hasSlots && !isSelected ? (
                            <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-primary-500" />
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="min-w-0 min-h-0 flex flex-col md:overflow-hidden">
                  <Text size="sm" className="font-medium text-gray-800 dark:text-gray-200 mb-2 shrink-0">
                    {selectedYmd
                      ? `Horários em ${formatBrDate(selectedYmd)}`
                      : 'Selecione um dia no calendário'}
                  </Text>
                  {!selectedYmd ? (
                    <Text size="sm" color="muted">
                      Escolha uma data com vaga à esquerda.
                    </Text>
                  ) : hoursGrouped.length === 0 ? (
                    <Text size="sm" color="muted">
                      Sem horários livres neste dia.
                    </Text>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 min-h-0 overflow-y-auto overscroll-contain pr-0.5">
                      {hoursGrouped.map(([horario, slots]) =>
                        slots.map((slot) => (
                          <button
                            key={`${slot.professionalId}-${horario}`}
                            type="button"
                            onClick={() => handlePickSlot(slot)}
                            title={slot.professionalName}
                            className="flex flex-col items-start gap-0.5 rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 py-1.5 text-left hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                          >
                            <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                              {horario}
                            </span>
                            <span className="text-[10px] leading-tight text-gray-600 dark:text-gray-300 truncate w-full">
                              {slot.professionalName}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
